import { useCallback, useId, useState } from 'react'
import { CornerDownLeft, Loader2, Plus, Square } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from '@/components/ui/input-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type LlmChatComposerProps = {
  onSend: (trimmedText: string) => void
  isSending: boolean
  canStopGenerating?: boolean
  onStopGenerating?: () => void
  modelOptions?: string[]
  selectedModel?: string
  onModelChange?: (model: string) => void
}

/**
 * Draft state is local so each keystroke only re-renders this component, not the
 * full page (sidebar, message list, header).
 */
export function LlmChatComposer({
  onSend,
  isSending,
  canStopGenerating = false,
  onStopGenerating,
  modelOptions = [],
  selectedModel,
  onModelChange,
}: LlmChatComposerProps) {
  const { t } = useTranslation('llmChat')
  const modelSelectId = useId()
  const [draft, setDraft] = useState('')

  const hasText = draft.trim().length > 0

  const handleSend = useCallback(() => {
    const text = draft.trim()
    if (!text || isSending) {
      return
    }
    setDraft('')
    onSend(text)
  }, [draft, isSending, onSend])

  return (
    <div className='shrink-0 px-4 pb-4'>
      <div className='mx-auto w-full'>
        <form
          className='w-full'
          onSubmit={(event) => {
            event.preventDefault()
            handleSend()
          }}
        >
          <input
            aria-label={t('uploadFiles')}
            className='hidden'
            disabled
            multiple
            title={t('uploadFiles')}
            type='file'
          />
          <InputGroup className='overflow-hidden'>
            <InputGroupTextarea
              className={cn(
                'max-h-48 min-h-16 flex-1 text-base md:text-sm',
                'placeholder:text-muted-foreground'
              )}
              name='message'
              placeholder={t('messagePlaceholder')}
              rows={1}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  handleSend()
                }
              }}
              disabled={isSending}
            />
            <InputGroupAddon
              align='block-end'
              className='justify-between gap-1 border-t border-border/60'
            >
              <div className='flex min-w-0 flex-1 items-center gap-1'>
                <InputGroupButton
                  aria-label={t('uploadFiles')}
                  disabled
                  size='icon-sm'
                  type='button'
                  variant='ghost'
                >
                  <Plus aria-hidden className='size-4' />
                </InputGroupButton>
                {modelOptions.length > 0 &&
                selectedModel !== undefined &&
                onModelChange !== undefined ? (
                  <>
                    <label className='sr-only' htmlFor={modelSelectId}>
                      {t('model')}
                    </label>
                    <Select
                      value={selectedModel}
                      onValueChange={(value) => {
                        if (value) {
                          onModelChange(value)
                        }
                      }}
                    >
                      <SelectTrigger
                        aria-label={t('model')}
                        className='h-8 w-max max-w-[min(100%,14rem)] shrink text-xs'
                        id={modelSelectId}
                        size='sm'
                      >
                        <SelectValue placeholder={t('model')} />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : null}
              </div>
              <div className='flex items-center gap-1'>
                {canStopGenerating && onStopGenerating ? (
                  <InputGroupButton
                    aria-label={t('stopGenerating')}
                    size='icon-sm'
                    type='button'
                    variant='secondary'
                    onClick={() => {
                      onStopGenerating()
                    }}
                  >
                    <Square aria-hidden className='size-4' />
                  </InputGroupButton>
                ) : null}
                <InputGroupButton
                  aria-label={t('sendMessage')}
                  disabled={isSending || !hasText}
                  size='icon-sm'
                  type='submit'
                  variant='default'
                >
                  {isSending ? (
                    <Loader2 aria-hidden className='size-4 animate-spin' />
                  ) : (
                    <CornerDownLeft
                      aria-hidden
                      className='size-4'
                      strokeWidth={2}
                    />
                  )}
                </InputGroupButton>
              </div>
            </InputGroupAddon>
          </InputGroup>
        </form>
      </div>
    </div>
  )
}
